import { Box, Flex, Grid, Text, VStack, useT } from '@minetrace/ui'
import type { IndiceMetadatosResponse } from '../../../api/models/response/indiceDigital'
import { m3, themeColors } from '../../../theme'
import { formatDate, formatDateTime } from '../../../lib/dates'
import { Field } from './ui'

/**
 * Metadatos del expediente (D.S. 029-2021-PCM art. 44).
 *
 * Aquí van datos del expediente, no explicaciones sobre ellos. Este tab llegó a
 * llevar un resumen por serie documental y un bloque de advertencias legales sobre
 * qué acredita y qué no acredita la firma; ambos se retiraron. El resumen por serie
 * mostraba plazos de retención que son criterio interno y no un plazo legal, y la
 * vigencia es del expediente completo, no de cada documento que se le adjunta.
 *
 * Los límites legales siguen imprimiéndose en el PDF del índice
 * (IndicePdfGenerator), que es donde tienen efecto frente a un tercero. La alerta
 * de sellado con clave efímera —que no es una explicación sino un defecto de una
 * versión concreta— se sigue mostrando en la cabecera y en el tab de Versiones.
 */
export function MetadatosTab({ metadatos }: { metadatos: IndiceMetadatosResponse }) {
  const { t } = useT()

  return (
    <VStack alignItems="stretch" gap={5}>
      <Panel
        title={t('expedientes.electronico.metadatos.title')}
        subtitle={t('expedientes.electronico.metadatos.subtitle')}
      >
        <Grid templateColumns="repeat(auto-fit, minmax(200px, 1fr))" gap={5}>
          <Field label={t('expedientes.electronico.metadatos.codigoUnico')} value={metadatos.codigoUnico} mono />
          <Field label={t('expedientes.electronico.type')} value={metadatos.tipoNombre} />
          <Field label="Estado" value={metadatos.estadoNombre} />
          <Field label={t('expedientes.electronico.cuid')} value={metadatos.cuidCode ?? '—'} mono />
          <Field
            label={t('expedientes.electronico.producer')}
            value={metadatos.entidadProductora ?? '—'}
          />
          <Field
            label={`${t('expedientes.electronico.producer')} · ${t('expedientes.electronico.ruc')}`}
            value={metadatos.entidadProductoraRuc ?? '—'}
            mono
          />
          <Field label="Titular" value={metadatos.titularRazonSocial ?? '—'} />
          <Field label={t('expedientes.electronico.ruc')} value={metadatos.titularRuc ?? '—'} mono />
          <Field label={t('expedientes.electronico.opened')} value={formatDate(metadatos.fechaApertura)} />
          <Field
            label={t('expedientes.electronico.lastUpdate')}
            value={formatDateTime(metadatos.fechaUltimaActualizacion)}
          />
          <Field label={t('expedientes.electronico.closed')} value={formatDate(metadatos.fechaCierre)} />
          <Field label={t('expedientes.electronico.metadatos.jurisdiccion')} value={metadatos.jurisdiccion} />
          <Field label={t('expedientes.electronico.metadatos.idioma')} value={metadatos.idioma} />
          <Field
            label={t('expedientes.electronico.metadatos.chainHead')}
            value={metadatos.chainHead ?? '—'}
            mono
          />
        </Grid>
      </Panel>
    </VStack>
  )
}

function Panel({
  title,
  subtitle,
  icon,
  children,
}: {
  title: string
  subtitle?: string
  icon?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <Box
      bg={themeColors.bgPanel}
      border="1px solid"
      borderColor={themeColors.border}
      borderRadius="xl"
      p={5}
    >
      <Flex alignItems="center" gap={2} mb={subtitle ? 1 : 4}>
        {icon && <Box color={m3.primaryContainer}>{icon}</Box>}
        <Text fontSize="sm" fontWeight="700" color={themeColors.text}>{title}</Text>
      </Flex>
      {subtitle && (
        <Text fontSize="xs" color={themeColors.textMuted} mb={4}>{subtitle}</Text>
      )}
      {children}
    </Box>
  )
}
